import { ChevronDown, FlaskConical, Info } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate } from 'react-router'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isProduction } from '@/config/config'
import { cn } from '@/lib/utils'
import { useCurrentUser } from '@/models/user'

/** The two canonical statuses carry an expectation; any other value renders as plain text. */
const STATUS_TOOLTIPS: Record<string, string> = {
  'Front-end prototype':
    'Uses real components, but adds mock data and latency as needed. Expect rough edges and dead ends.',
  'Pre-production prototype':
    'Uses real components, real data where possible, logs real events in PostHog, and handles edge cases and errors.',
}

interface PrototypeFrameProps {
  title: string
  /** Who's driving this prototype — shown in the banner so reviewers know who to send feedback to. */
  owner?: string
  date?: string
  status?: string
  /**
   * Prototype-specific context (goal, project, feedback links, next steps).
   * Rendered in the dark banner so it stays visually distinct from the actual
   * prototype UI below — some prototypes are entire screens, so that
   * production-vs-prototype boundary needs to be unambiguous.
   */
  brief?: React.ReactNode
  /**
   * Renders children edge-to-edge instead of inside the padded max-w
   * container — for full-page prototypes that ship their own nav/footer,
   * as opposed to a single component shown on a plain canvas.
   */
  fullBleedContent?: boolean
  /**
   * Drops the banner for a prototype that is meant to be looked at as the
   * product itself. The staff gate below still applies — that is why such a
   * page still comes through here rather than rendering on its own.
   */
  hideBanner?: boolean
  children: React.ReactNode
}

/**
 * Chrome wrapped around every design-system prototype page, opened in its own
 * tab from the "Prototypes" nav section. Signals to internal users that
 * they're on an exploratory page — not a shipped feature. Same access rule as
 * the design-system page itself: staff-only in production, open in dev/staging.
 */
export function PrototypeFrame({
  title,
  owner,
  date,
  status,
  brief,
  fullBleedContent,
  hideBanner,
  children,
}: PrototypeFrameProps) {
  const { user } = useCurrentUser({ enabled: isProduction })
  // Collapsed by default so the prototype, not its brief, is the first thing on screen.
  const [isBriefOpen, setIsBriefOpen] = useState(false)
  const hasBrief = !!(owner || date || status || brief)

  if (isProduction && !user?.isCaiAdmin) {
    return <Navigate to="/insure" replace />
  }

  if (hideBanner) return children

  return (
    <div className="bg-ink-50 min-h-screen shrink-0">
      <div className="bg-ink-900 text-ink-50">
        <div className="bg-ink-900 sticky top-0 z-10">
          <div className="mx-auto flex max-w-(--maxw) flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 sm:px-6 2xl:max-w-[1800px]">
            <FlaskConical className="size-4 shrink-0" />
            <span className="min-w-0 truncate font-mono text-xs tracking-[0.08em] uppercase">
              Prototype: {title}
            </span>
            {/* One group, so on a phone both controls drop to their own row together. */}
            <div className="ml-auto flex shrink-0 items-center gap-4">
              {hasBrief && (
                <Button
                  variant="link"
                  size="xs"
                  aria-expanded={isBriefOpen}
                  onClick={() => setIsBriefOpen((open) => !open)}
                  className="text-ink-300 hover:text-ink-50 h-auto px-0 text-xs"
                >
                  {isBriefOpen ? 'Hide brief' : 'Show brief'}
                  <ChevronDown className={cn('size-3 transition-transform', isBriefOpen && 'rotate-180')} />
                </Button>
              )}
              <Link
                to="/design-system"
                className="text-ink-300 hover:text-ink-50 text-xs underline underline-offset-2"
              >
                Back to Design System
              </Link>
            </div>
          </div>
        </div>
        {hasBrief && isBriefOpen && (
          <div className="mx-auto max-w-(--maxw) px-4 pb-8 sm:px-6 2xl:max-w-[1800px]">
            {(owner || date || status) && (
              <div className="text-ink-400 flex flex-wrap gap-x-3 text-xs">
                {owner && <span>Owner: {owner}</span>}
                {date && <span>Date: {date}</span>}
                {status && (
                  <span className="flex items-center gap-1">
                    Status: {status}
                    {STATUS_TOOLTIPS[status] && (
                      <Tooltip>
                        <TooltipTrigger aria-label={`What ${status} means`}>
                          <Info className="size-3" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{STATUS_TOOLTIPS[status]}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                )}
              </div>
            )}
            {brief && <div className="mt-6">{brief}</div>}
          </div>
        )}
      </div>
      {fullBleedContent ? (
        children
      ) : (
        <div className="mx-auto max-w-(--maxw) px-4 py-10 sm:px-6 2xl:max-w-[1800px]">{children}</div>
      )}
    </div>
  )
}
